import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {State} from '../../../../types/mx/reducers';
import SelectSearchComponent from '../../../../../common/components/SelectSearchComponent';
import {CHATS} from '../../../constants/LabelConstants';
import {ERROR_REQUIRED} from '../../../../constants/ValidateConstant';
import Button from '../../../../../common/components/Button';
import * as ModalActions from '../../../../../common/actions/ModalActions';
import {Creators as ChatDataActions} from '../../../reducers/chat/data';
import {Doc} from '../../../types/reducers';
import {PDF_EXTENSION, PDF_MIME_TYPE} from '../../../../../common/constants/MimeTypeConstants';
import {bytesToHumanFileSize} from '../../../../../common/utils/Utils';
import {formatDateCustomized} from '../../../../../common/utils/DateUtils';
import AssetsManager from '../../../../../common/utils/AssetsManager';
import {downloadFileFromURL} from '../../../../../common/utils/GeneralUtils';
import {getExtension} from '../../../../../common/utils/UrlUtils';

interface ChatsAssignDocViewProps {
    data: {
        doc: Doc;
    };
}

const ChatsAssignDocView: React.FC<ChatsAssignDocViewProps> = ({
    data: {doc},
}): React.ReactElement => {
    const dispatch = useDispatch();
    const documentTypes = useSelector(
        (state: State) =>
            state.commonLoanApplication.docs.catalogs.documentTypes,
    );
    const [documentTypeSelected, setDocumentTypeSelected] =
        React.useState(null);
    const [showError, setShowError] = React.useState(false);
    const onSelectDocumentType = (value) => {
        setShowError(true);
        setDocumentTypeSelected(value);
    };
    const onSubmitDocumentType = () => {
        dispatch(ChatDataActions.uploadDocument(doc, documentTypeSelected));
        dispatch(ModalActions.CLOSE_MODAL_ACTION.action());
        setDocumentTypeSelected(null);
    };
    const onDownload = (item) => {
        const type =
            item.mimeType === PDF_MIME_TYPE || item.originalUrl.indexOf(PDF_EXTENSION) >= 0 ? PDF_MIME_TYPE : '';
        return downloadFileFromURL(item.originalUrl, item.fileName, type);
    }
    return (
        <div className="container px-0">
            <div className="row">
                {doc.assigned ? (
                    <div className="col-12 text-center">
                        <div className="p-1 my-2 status-label text-secondary">
                            <small>
                                Asignado como {doc.assigned.label} por{' '}
                                {doc.assigned.assignedBy}:{' '}
                                {formatDateCustomized(
                                    doc.assigned.assignedAt,
                                )}
                            </small>
                            {doc.assigned.deleted && (
                                <>
                                    <hr className="my-1 w-75" />
                                    <small className="text-xs secondary-lighten-1">
                                        {CHATS.DELETED_DOC_FROM_LOAN_ATTACHMENT_MSG(
                                            doc.assigned.deleted.by,
                                            formatDateCustomized(
                                                doc.assigned.deleted.date,
                                            ),
                                        )}
                                    </small>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="col-12">
                        <p className="regular-text">
                            {CHATS.UPLOAD_FILES_LABEL}
                        </p>
                        <SelectSearchComponent
                            id="documentTypes"
                            customClass="py-4"
                            required={true}
                            filter={true}
                            hasError={showError && !documentTypeSelected}
                            labelError={ERROR_REQUIRED}
                            placeholder="Selecciona el tipo de documento"
                            label={CHATS.UPLOAD_DOCUMENT_TYPE_LABEL}
                            data={documentTypes
                                .filter((item) => !item.autogenerated)
                                .map((item) => ({...item, value: item.label}))
                                .sort((a, b) => a.label.localeCompare(b.label))}
                            value={documentTypeSelected}
                            onChange={onSelectDocumentType}
                        />
                    </div>
                )}
                <div className="col-12 d-flex justify-content-center">
                    {doc.mimeType === PDF_MIME_TYPE ? (
                        <div className="pdf-wrapper mb-4">
                            <object className="pdf-object"
                                data={`${doc.originalUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0`}
                            />
                        </div>
                    ) : (
                        <img
                            className="max-h-60-screen img-fluid pb-3"
                            src={doc.thumbnailUrl}
                        />
                    )}
                </div>
                <div className="col-12 d-flex justify-content-center pt-0 pb-4">
                    <div className="list-group list-group-flush w-75 small">
                        <div className="row">
                            <div className="col-2 font-weight-bold">autor</div>
                            <div className="col-10">{doc.authorName}</div>
                        </div>
                        <div className="row py-1">
                            <div className="col-6">
                                <div className="row">
                                    <div className="col-4 font-weight-bold">
                                        fecha
                                    </div>
                                    <div className="col-8">{doc.createdAt}</div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="row">
                                    <div className="col-4 font-weight-bold">
                                        tamaño
                                    </div>
                                    <div className="col-8">
                                        {bytesToHumanFileSize(doc.originalSize)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <div className="row">
                                    <div className="col-4 font-weight-bold">
                                        tipo
                                    </div>
                                    <div className="col-8">
                                        {getExtension(doc.fileName)}
                                    </div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div
                                    className="row chat-actions"
                                    onClick={() => onDownload(doc)}>
                                    <div className="col-4 font-weight-bold">
                                        descargar
                                    </div>
                                    <div className="col-8">
                                        <img
                                            src={AssetsManager(
                                                '/assets/images/common/upload-file.svg',
                                            )}
                                            alt="descargar"
                                            width="22px"
                                            height="22px"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {!doc.assigned && (
                    <div className="col-12">
                        <Button
                            disabled={!documentTypeSelected}
                            type="squared"
                            customClass="w-100"
                            text={CHATS.ASSIGN_DOC}
                            onPress={onSubmitDocumentType}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatsAssignDocView;
